import React from 'react';
import {CheckCircle, XCircle, AlertTriangle, Info, X} from 'lucide-react';
import {useAdminNotifications, Notification, NotificationType} from '@/modules/admin/contexts/AdminNotificationContext';

interface NotificationBannerProps {
    notification: Notification;
}

const getNotificationStyles = (type: NotificationType) => {
    switch (type) {
        case 'success':
            return {
                containerClass: 'bg-mld-success/5 border-mld-success/20 text-mld-success',
                iconClass: 'text-mld-success',
                icon: CheckCircle,
            };
        case 'error':
            return {
                containerClass: 'bg-mld-danger/5 border-mld-danger/20 text-mld-danger',
                iconClass: 'text-mld-danger',
                icon: XCircle,
            };
        case 'warning':
            return {
                containerClass: 'bg-yellow-50 border-yellow-200 text-yellow-800',
                iconClass: 'text-yellow-600',
                icon: AlertTriangle,
            };
        case 'info':
            return {
                containerClass: 'bg-mld-info/5 border-mld-info/20 text-mld-info',
                iconClass: 'text-mld-info',
                icon: Info,
            };
        default:
            return {
                containerClass: 'bg-mld-menu-background border-mld-panel-border text-mld-text-primary',
                iconClass: 'text-mld-text-secondary',
                icon: Info,
            };
    }
};

const NotificationBanner: React.FC<NotificationBannerProps> = ({notification}) => {
    const {dismissNotification} = useAdminNotifications();
    const {containerClass, iconClass, icon: Icon} = getNotificationStyles(notification.type);

    return (
        <div className={`border rounded-lg p-4 mb-4 ${containerClass}`}>
            <div className="flex items-start">
                <Icon className={`h-5 w-5 mt-0.5 mr-3 flex-shrink-0 ${iconClass}`} />
                <div className="flex-1">
                    {notification.title && <h3 className="text-sm font-medium mb-1">{notification.title}</h3>}
                    <p className="text-sm">{notification.message}</p>
                </div>
                <button
                    onClick={() => dismissNotification(notification.id)}
                    className="ml-3 flex-shrink-0 hover:opacity-70 transition-opacity"
                    aria-label="Dismiss notification">
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};

interface AdminNotificationBannerProps {
    className?: string;
}

export const AdminNotificationBanner: React.FC<AdminNotificationBannerProps> = ({className = ''}) => {
    const {notifications} = useAdminNotifications();

    if (notifications.length === 0) {
        return null;
    }

    return (
        <div className={`space-y-2 ${className}`}>
            {notifications.map((notification) => (
                <NotificationBanner key={notification.id} notification={notification} />
            ))}
        </div>
    );
};

export default AdminNotificationBanner;
